import React, { useState, useEffect, useMemo } from 'react';
import './App.css';
import Navbar from './components/navbar';
import Homepage from './components/home/homepage';
import NotFound from './components/404';
import { CreateLeague, ViewLeagueDetails } from './components/leagues/';
import AllTeams from './components/ipl/all-teams';
import SingleTeam from './components/ipl/single-team';
import Players from './components/ipl/players';
import Captains from './components/ipl/captains';
import { Switch, Route, withRouter } from "react-router-dom";
import Privacy from './components/privacy';
import TOS from './components/tos';
import Profile from './components/profile/profile';
import { ThemeProvider } from '@material-ui/core';
import { createTheme } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import { ViewToken } from './components/view-token';
import SquadView from './components/ipl/squad-view';
import OverwriteXI from './components/ipl/overwrite-xi';
import SuperAdmin from './components/admin/super-admin';
import AuctionView from './components/ipl/auction-view';
import AuctionLog from './components/ipl/auction-log';
import SnakeDraftView from './components/ipl/snake-draft-view';
import SnakeDraftOrder from './components/ipl/snake-draft-order';

function App() {
    const [darkMode, setDarkMode] = useState(() => {
        const savedMode = localStorage.getItem('darkMode');
        if (savedMode !== null) {
            return savedMode === 'true';
        }
        return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    });

    useEffect(() => {
        localStorage.setItem('darkMode', darkMode);
    }, [darkMode]);

    const handleThemeToggle = () => {
        setDarkMode(prev => !prev);
    };

    const theme = useMemo(() => createTheme({
        palette: {
            type: darkMode ? 'dark' : 'light',
            primary: {
                main: '#1f5fbf',
            },
            secondary: {
                main: '#16a085',
            },
            info: {
                main: darkMode ? '#93c5fd' : '#1f5fbf',
            },
            background: {
                default: darkMode ? '#111827' : '#f5f7fb',
                paper: darkMode ? '#1f2937' : '#ffffff',
            },
        },
        typography: {
            fontFamily: [
                '-apple-system',
                'BlinkMacSystemFont',
                '"Segoe UI"',
                'Roboto',
                '"Helvetica Neue"',
                'Arial',
                'sans-serif',
            ].join(','),
            h5: {
                fontWeight: 600,
            },
            h6: {
                fontWeight: 600,
            },
            button: {
                fontWeight: 600,
                textTransform: 'none',
                letterSpacing: 0,
            },
        },
        shape: {
            borderRadius: 8,
        },
        overrides: {
            MuiAppBar: {
                colorPrimary: {
                    backgroundColor: darkMode ? '#172554' : '#1f5fbf',
                },
            },
            MuiButton: {
                root: {
                    borderRadius: 8,
                    minHeight: 40,
                },
                contained: {
                    boxShadow: 'none',
                    '&:hover': {
                        boxShadow: 'none',
                    },
                },
            },
            MuiCard: {
                root: {
                    borderRadius: 8,
                    boxShadow: darkMode
                        ? '0 12px 32px rgba(0, 0, 0, 0.28)'
                        : '0 10px 28px rgba(15, 23, 42, 0.08)',
                    border: darkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(15, 23, 42, 0.06)',
                },
            },
            MuiPaper: {
                rounded: {
                    borderRadius: 8,
                },
            },
            MuiTableCell: {
                root: {
                    borderBottom: darkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(15, 23, 42, 0.08)',
                },
                head: {
                    fontWeight: 600,
                },
            },
            MuiTextField: {
                root: {
                    marginTop: 8,
                    marginBottom: 8,
                },
            },
            MuiCssBaseline: {
                '@global': {
                    a: {
                        color: darkMode ? '#93c5fd' : '#1f5fbf',
                        fontWeight: 500,
                    },
                    'a:visited': {
                        color: darkMode ? '#bfdbfe' : '#174a96',
                    },
                },
            },
        },
    }), [darkMode]);

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <div className="App">
                <Navbar darkMode={darkMode} onToggle={handleThemeToggle} />
                <Switch>
                    <Route exact path="/" component={Homepage} />
                    <Route path="/leagues/create" component={CreateLeague} />
                    <Route path="/leagues/:leagueID/admin" render={(props) => <ViewLeagueDetails {...props} type="admin" />} />
                    <Route path="/leagues/:leagueID/settings" render={(props) => <ViewLeagueDetails {...props} type="settings" />} />
                    <Route path="/leagues/:leagueID/auction/log" component={AuctionLog} />
                    <Route path="/leagues/:leagueID/auction" component={AuctionView} />
                    <Route path="/leagues/:leagueID/snake-draft/order" component={SnakeDraftOrder} />
                    <Route path="/leagues/:leagueID/snake-draft" component={SnakeDraftView} />
                    <Route path="/leagues/:leagueID/teams/:teamID/squad" component={SquadView} />
                    <Route path="/leagues/:leagueID/teams/:teamID/overwrite-xi" component={OverwriteXI} />
                    <Route path="/leagues/:leagueID/teams/:teamID/captains" component={withRouter(Captains)} />
                    <Route path="/leagues/:leagueID/teams/:teamID" component={SingleTeam} />
                    <Route path="/leagues/:leagueID" component={AllTeams} />
                    <Route path="/players/:tournament/:leagueID" component={Players} />
                    <Route path="/invite/:tokenCode" component={ViewToken} />
                    <Route path="/admin" component={SuperAdmin} />
                    <Route path="/profile" component={Profile} />
                    <Route path="/privacy" component={Privacy} />
                    <Route path="/tos" component={TOS} />
                    <Route path="*" component={NotFound} />
                </Switch>
            </div>
        </ThemeProvider>
    );
}

export default App;
